import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { IdentityAdminService, IdentityRole } from './identity-admin.service';
import { PlatformAdminService, TenantRecord } from '../platform/platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-identity-roles',
  imports: [AinPageComponent, AinSearchAccordionComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, InputTextModule, ButtonModule, DialogModule, RouterLink],
  template: `
    <ain-page [eyebrow]="t('identity.eyebrow')" [title]="t('identity.rolesPage.title')" [description]="t('identity.rolesPage.description')">
      <div page-actions>
        <ain-info-hint
          titleKey="identity.rolesPage.hint.title"
          descriptionKey="identity.rolesPage.hint.description"
          [bulletKeys]="['identity.rolesPage.hint.bullet1', 'identity.rolesPage.hint.bullet2', 'identity.rolesPage.hint.bullet3']" />
        <button pButton type="button" [label]="t('identity.governance.createRole')" (click)="createDialogVisible.set(true)"></button>
      </div>

      <ain-search-accordion [title]="t('platform.search.title')">
        <section class="search-grid">
          <input pInputText [(ngModel)]="filters.query" [placeholder]="t('platform.search.query')" />
          @if (isPlatformAdmin()) {
            <select [(ngModel)]="filters.tenantId">
              <option value="">{{ t('identity.rolesPage.anyTenant') }}</option>
              @for (tenant of tenants(); track tenant.id) {
                <option [value]="tenant.id">{{ tenant.name }}</option>
              }
            </select>
          }
          <select [(ngModel)]="filters.system">
            <option value="">{{ t('identity.rolesPage.anyRoleType') }}</option>
            <option value="true">{{ t('identity.rolesPage.systemRole') }}</option>
            <option value="false">{{ t('identity.rolesPage.customRole') }}</option>
          </select>
          <button pButton type="button" [label]="t('platform.search.apply')" (click)="reload()"></button>
          <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
        </section>
      </ain-search-accordion>

      @if (loading()) {
        <ain-state type="loading" [title]="t('identity.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('identity.unavailable')" [message]="error() ?? ''" />
      } @else {
        <p-table [value]="filteredRoles()">
          <ng-template pTemplate="header">
            <tr><th>{{ t('columns.name') }}</th><th>{{ t('columns.systemName') }}</th><th>{{ t('columns.tenant') }}</th><th>{{ t('identity.rolesPage.roleType') }}</th><th>{{ t('common.open') }}</th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.name }}</td>
              <td>{{ row.systemName }}</td>
              <td>{{ tenantName(row.tenantId) }}</td>
              <td>{{ row.isSystemRole ? t('identity.rolesPage.systemRole') : t('identity.rolesPage.customRole') }}</td>
              <td><a [routerLink]="['/identity/roles', row.id, 'permissions']">{{ t('identity.rolesPage.permissions') }}</a></td>
            </tr>
          </ng-template>
        </p-table>
      }

      <p-dialog [header]="t('identity.governance.createRole')" [visible]="createDialogVisible()" [modal]="true" [style]="{ width: '48rem', maxWidth: '95vw' }" (visibleChange)="createDialogVisible.set($event)">
        <section class="create-grid">
          @if (isPlatformAdmin()) {
            <select [(ngModel)]="createModel.tenantId">
              <option value="">{{ t('common.select') }}</option>
              @for (tenant of tenants(); track tenant.id) {
                <option [value]="tenant.id">{{ tenant.name }}</option>
              }
            </select>
          }
          <input pInputText [(ngModel)]="createModel.name" [placeholder]="t('columns.name')" />
          <input pInputText [(ngModel)]="createModel.systemName" [placeholder]="t('columns.systemName')" />
          <button pButton type="button" [label]="t('common.save')" (click)="createRole()"></button>
        </section>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr 1.4fr 1fr auto auto; gap: 8px; align-items: center; }
    .search-grid select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; min-height: 40px; }
    .create-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
    .create-grid select { border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px; min-height: 40px; }
    @media (max-width: 1100px) { .search-grid, .create-grid { grid-template-columns: 1fr; } }
    a { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
  `]
})
export class IdentityRolesComponent implements OnInit {
  private readonly api = inject(IdentityAdminService);
  private readonly platformApi = inject(PlatformAdminService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly localizationService = inject(LocalizationService);

  readonly rows = signal<IdentityRole[]>([]);
  readonly tenants = signal<TenantRecord[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly createDialogVisible = signal(false);
  readonly currentTenantId = signal<string>('');
  readonly filteredRoles = computed(() => {
    const query = this.filters.query.trim().toLowerCase();
    const system = this.filters.system.trim();
    return this.rows().filter(row => {
      const matchesQuery = !query || row.name.toLowerCase().includes(query) || row.systemName.toLowerCase().includes(query);
      const matchesSystem = !system || `${row.isSystemRole}` === system;
      return matchesQuery && matchesSystem;
    });
  });

  filters = { query: '', tenantId: '', system: '' };
  createModel = { tenantId: '', name: '', systemName: '' };

  ngOnInit(): void {
    this.currentContextService.loadContext().subscribe({
      next: context => {
        const tenantId = context.tenantId?.trim() ?? '';
        this.currentTenantId.set(tenantId);
        if (!context.isPlatformAdmin && tenantId) {
          this.createModel.tenantId = tenantId;
        }
        this.reload();
      },
      error: () => this.reload()
    });
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    let completed = 0;
    const markLoaded = (): void => {
      completed += 1;
      if (completed >= 2) this.loading.set(false);
    };

    this.api.listRoles({
      tenantId: this.effectiveListTenantId(),
      query: this.filters.query.trim(),
      isSystemRole: this.filters.system.trim() || undefined,
      skip: 0,
      take: 300
    }).subscribe({
      next: rows => {
        this.rows.set(rows.items);
        markLoaded();
      },
      error: () => {
        this.error.set(this.t('identity.errorRoles'));
        this.loading.set(false);
      }
    });

    if (this.isPlatformAdmin()) {
      this.platformApi.listTenants({ skip: 0, take: 300 }).subscribe({
        next: rows => {
          this.tenants.set(rows.items);
          markLoaded();
        },
        error: () => {
          this.error.set(this.t('platform.tenants.errorLoad'));
          this.loading.set(false);
        }
      });
    } else {
      const tenantId = this.currentTenantId().trim();
      if (tenantId) {
        this.createModel.tenantId = tenantId;
      }
      markLoaded();
    }
  }

  createRole(): void {
    const payload = {
      tenantId: this.isPlatformAdmin()
        ? this.createModel.tenantId.trim()
        : this.currentTenantId().trim(),
      name: this.createModel.name.trim(),
      systemName: this.createModel.systemName.trim(),
      description: '',
      isSystemRole: false
    };
    if (!payload.tenantId || !payload.name || !payload.systemName) return;

    this.api.createRole(payload).subscribe({
      next: created => {
        this.rows.update(rows => [created, ...rows]);
        this.createModel.name = '';
        this.createModel.systemName = '';
        this.filters.tenantId = created.tenantId;
        this.createDialogVisible.set(false);
      },
      error: () => this.error.set(this.t('identity.errorRoles'))
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.filters.tenantId = '';
    this.filters.system = '';
    this.reload();
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  tenantName(tenantId: string): string {
    return this.tenants().find(item => item.id === tenantId)?.name ?? tenantId;
  }

  isPlatformAdmin(): boolean {
    return this.currentContextService.context()?.isPlatformAdmin === true;
  }

  private effectiveListTenantId(): string | undefined {
    if (this.isPlatformAdmin()) {
      return this.filters.tenantId.trim() || undefined;
    }
    return this.currentTenantId().trim() || undefined;
  }
}
